"use client";

import { useLinkPreview } from "@/lib/hooks/useLinkPreview";
import { LINK_CARD_FRAME } from "./LinkPreviewCard";

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function Skeleton() {
  return (
    <div className={`${LINK_CARD_FRAME} rounded-xl border border-border bg-surface overflow-hidden animate-pulse`}>
      <div className="flex">
        <div className="flex-1 p-3 space-y-2">
          <div className="h-3 w-24 bg-muted rounded" />
          <div className="h-4 w-3/4 bg-muted rounded" />
          <div className="h-3 w-full bg-muted rounded" />
        </div>
        <div className="w-[120px] h-[90px] bg-muted shrink-0" />
      </div>
    </div>
  );
}

function FallbackLink({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${LINK_CARD_FRAME} block rounded-xl border border-border bg-surface px-3 py-2.5 text-sm text-primary hover:bg-surface-2 transition-colors truncate`}
    >
      {getDomain(url)}
    </a>
  );
}

/**
 * Metadata the caller already has, so the card can render without a round-trip.
 * A resolved Discourse onebox carries exactly this — see `extractOneboxes`.
 */
interface LinkPreviewSeed {
  title?: string | null;
  description?: string | null;
  image?: string | null;
  favicon?: string | null;
}

interface LinkPreviewProps {
  url: string;
  /**
   * Pre-resolved metadata. When it has a title the card renders straight from
   * it and `/api/og` is never called — that saves a request per link per
   * render, and stops a site that blocks our fetcher from *downgrading* a card
   * we already had the content for to a bare domain link.
   */
  seed?: LinkPreviewSeed;
}

/**
 * The onebox shape: text left, thumbnail right. Migrated posts still carry the
 * markup this reads, so it renders in the same feed as `ThetaShareCard` and
 * takes the same frame and the same surface — two cards a scroll apart that
 * disagreed about their width would read as one of them being broken.
 */
export function LinkPreview({ url, seed }: LinkPreviewProps) {
  const seeded = Boolean(seed?.title);
  const { data, isLoading, isError } = useLinkPreview(url, !seeded);

  if (seeded) return <Card url={url} data={seed!} />;
  if (isLoading) return <Skeleton />;
  if (isError || !data?.title) return <FallbackLink url={url} />;

  return <Card url={url} data={data} />;
}

function Card({ url, data }: { url: string; data: LinkPreviewSeed }) {
  const domain = getDomain(url);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${LINK_CARD_FRAME} block rounded-xl border border-border bg-surface overflow-hidden hover:bg-surface-2 transition-colors`}
    >
      <div className="flex">
        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            {data.favicon && (
              <img
                src={data.favicon}
                alt=""
                className="w-4 h-4 rounded-sm"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            )}
            <span className="text-xs text-muted-foreground truncate">{domain}</span>
          </div>
          <div className="font-medium text-sm text-foreground line-clamp-2 leading-snug">
            {data.title}
          </div>
          {data.description && (
            <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5 leading-relaxed">
              {data.description}
            </div>
          )}
        </div>
        {data.image && (
          <div className="w-[120px] shrink-0">
            <img
              src={data.image}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = "none"; }}
            />
          </div>
        )}
      </div>
    </a>
  );
}
